import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { tacticalApi, managerApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Toaster, toast } from 'sonner';
import { 
  Plus, 
  FileText, 
  Users, 
  Calendar, 
  LogOut, 
  Bell,
  ChevronRight,
  Archive,
  Eye,
  ArrowLeft
} from 'lucide-react';
import TacticalEditor from '../components/TacticalEditor';
import ManagerRequests from '../components/ManagerRequests';
import ManagerDashboard from '../components/ManagerDashboard';
import ArchiveView from '../components/ArchiveView';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [currentTactical, setCurrentTactical] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState('dashboard'); // dashboard, tactical, requests, manager, archives
  const [pendingRequests, setPendingRequests] = useState([]);
  const [subordinates, setSubordinates] = useState([]);

  // Check if we're in impersonation mode
  const impersonatedUser = sessionStorage.getItem('impersonating_user')
    ? JSON.parse(sessionStorage.getItem('impersonating_user'))
    : null;
  const isImpersonating = !!sessionStorage.getItem('admin_return_token');

  const handleReturnToAdmin = () => {
    // Clear the impersonation token
    localStorage.removeItem('tactical_token');
    sessionStorage.removeItem('impersonating_user');
    sessionStorage.removeItem('admin_return_token');
    // Navigate to admin page
    window.location.href = window.location.origin + '/admin';
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tactical, requests, subs] = await Promise.all([
        tacticalApi.getCurrent().catch(() => null),
        managerApi.getRequests().catch(() => []),
        managerApi.getSubordinates().catch(() => []),
      ]);
      setCurrentTactical(tactical);
      setPendingRequests(requests);
      setSubordinates(subs);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTactical = async () => {
    setCreating(true);
    try {
      const tactical = await tacticalApi.create();
      setCurrentTactical(tactical);
      setView('tactical');
      toast.success('Tactical created successfully');
    } catch (err) {
      console.error('Failed to create tactical:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create tactical';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const now = new Date();
  const currentMonthName = MONTHS[now.getMonth()];
  const currentYear = now.getFullYear();

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8" data-testid="dashboard-loading">
        <div className="max-w-6xl mx-auto">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        </div>
      </div>
    );
  }

  // Impersonation banner component (reusable across views)
  const ImpersonationBanner = () => {
    if (!isImpersonating) return null;
    return (
      <div className="bg-amber-500 text-white px-4 py-2.5 sticky top-0 z-50" data-testid="impersonation-banner">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5" />
            <span className="font-medium text-sm">
              Admin View — You are viewing as <strong>{impersonatedUser?.display_name || user?.display_name}</strong> ({impersonatedUser?.email || user?.email})
            </span>
          </div>
          <button
            onClick={handleReturnToAdmin}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
            data-testid="return-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Return to Admin
          </button>
        </div>
      </div>
    );
  };

  // Render different views
  if (view === 'tactical' && currentTactical) {
    return (
      <>
        <ImpersonationBanner />
        <TacticalEditor 
          tactical={currentTactical} 
          onBack={() => {
            loadData();
            setView('dashboard');
          }}
          onUpdate={setCurrentTactical}
          isOwner={true}
        />
      </>
    );
  }

  if (view === 'requests') {
    return (
      <>
        <ImpersonationBanner />
        <ManagerRequests 
          onBack={() => {
            loadData();
            setView('dashboard');
          }}
        />
      </>
    );
  }

  if (view === 'manager') {
    return (
      <>
        <ImpersonationBanner />
        <ManagerDashboard 
          subordinates={subordinates}
          onBack={() => {
            loadData();
            setView('dashboard');
          }}
        />
      </>
    );
  }

  if (view === 'archives') {
    return (
      <>
        <ImpersonationBanner />
        <ArchiveView 
          onBack={() => setView('dashboard')}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      <Toaster position="top-right" />
      
      {/* Impersonation Banner */}
      <ImpersonationBanner />

      {/* Header */}
      <header className={`bg-white border-b border-slate-200 sticky ${isImpersonating ? 'top-[44px]' : 'top-0'} z-10`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Tacticals</h2>
              <p className="text-xs text-slate-500">Monthly performance tracking</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {pendingRequests.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="relative"
                onClick={() => setView('requests')}
                data-testid="requests-badge"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs rounded-full flex items-center justify-center">
                  {pendingRequests.length}
                </span>
              </Button>
            )}
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{user?.display_name}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground mb-2">
            Welcome, {user?.given_name || user?.display_name}
          </h1>
          <p className="text-muted-foreground">
            {currentMonthName} {currentYear} — Track your professional development and goals.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Current Tactical Card */}
          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Calendar className="w-5 h-5 text-primary" />
                <Badge variant="secondary" className="bg-secondary text-secondary-foreground">
                  {currentMonthName}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <CardTitle className="text-lg mb-1">Current Tactical</CardTitle>
              <CardDescription>
                {currentTactical 
                  ? `Last updated ${new Date(currentTactical.updated_at).toLocaleDateString()}`
                  : 'No tactical created yet'
                }
              </CardDescription>
              {currentTactical ? (
                <Button 
                  className="w-full mt-4 btn-hover"
                  onClick={() => setView('tactical')}
                  data-testid="view-tactical-button"
                >
                  View Tactical
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  className="w-full mt-4 btn-hover"
                  onClick={handleCreateTactical}
                  disabled={creating}
                  data-testid="create-tactical-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {creating ? 'Creating...' : 'Create Tactical'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Manager Requests Card */}
          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Bell className="w-5 h-5 text-accent" />
                {pendingRequests.length > 0 && (
                  <Badge className="bg-accent text-accent-foreground">
                    {pendingRequests.length} pending
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <CardTitle className="text-lg mb-1">Manager Requests</CardTitle>
              <CardDescription>
                {pendingRequests.length > 0 
                  ? `${pendingRequests.length} request(s) awaiting your response`
                  : 'No pending requests'
                }
              </CardDescription>
              <Button 
                variant="outline"
                className="w-full mt-4"
                onClick={() => setView('requests')}
                data-testid="view-requests-button"
              >
                View Requests
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Team Management Card */}
          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Users className="w-5 h-5 text-emerald-600" />
                {subordinates.length > 0 && (
                  <Badge variant="secondary">
                    {subordinates.length} team member{subordinates.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <CardTitle className="text-lg mb-1">Team Tacticals</CardTitle>
              <CardDescription>
                {subordinates.length > 0 
                  ? `View and manage ${subordinates.length} team member tactical(s)`
                  : 'No team members assigned yet'
                }
              </CardDescription>
              <Button 
                variant="outline"
                className="w-full mt-4"
                onClick={() => setView('manager')}
                disabled={subordinates.length === 0}
                data-testid="view-team-button"
              >
                Manage Team
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Archives Section */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Archive className="w-5 h-5 text-muted-foreground" />
                <div>
                  <CardTitle className="text-lg">Archives</CardTitle>
                  <CardDescription>View your past monthly tacticals</CardDescription>
                </div>
              </div>
              <Button 
                variant="ghost"
                onClick={() => setView('archives')}
                data-testid="view-archives-button"
              >
                View All
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard;
